import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Compass, MapPin, Clock, Waves, Satellite, Loader2, AlertTriangle, Gauge, Info } from "lucide-react";
import { fetchIslandDetail, sentinelUrl } from "@/lib/api";
import { riskOf, countdown } from "@/lib/geo";

const confColor = (c) => (c >= 70 ? "#00ff87" : c >= 45 ? "#FF9F1C" : "#94a3b8");
const PIN = { EXPOSED: "#FF9F1C", PARTIAL: "#FFD166", SHELTERED: "#94a3b8" };

const RING = ["North", "North-East", "East", "South-East", "South", "South-West", "West", "North-West"];

function CompassRose({ coast }) {
  const idx = RING.indexOf(coast);
  return (
    <div className="relative h-28 w-28 shrink-0">
      <div className="absolute inset-0 rounded-full border border-cyan-500/20" />
      <div className="absolute inset-3 rounded-full border border-white/5" />
      {RING.map((label, i) => {
        const ang = (i / 8) * Math.PI * 2 - Math.PI / 2;
        const x = 50 + Math.cos(ang) * 42;
        const y = 50 + Math.sin(ang) * 42;
        const active = i === idx;
        return (
          <span
            key={label}
            className={`absolute -translate-x-1/2 -translate-y-1/2 font-mono-tel text-[9px] ${
              active ? "font-bold text-amberx" : "text-slate-500"
            }`}
            style={{ left: `${x}%`, top: `${y}%` }}
          >
            {label.split("-").map((s) => s[0]).join("")}
          </span>
        );
      })}
      {idx >= 0 && (
        <div
          className="absolute left-1/2 top-1/2 h-[38%] w-[3px] origin-bottom rounded-full"
          style={{
            background: "linear-gradient(to top,#00e5ff,#FF9F1C)",
            transform: `translate(-50%,-100%) rotate(${(idx / 8) * 360}deg)`,
            transformOrigin: "bottom center",
            boxShadow: "0 0 10px rgba(255,159,28,0.6)",
          }}
        />
      )}
      <div className="absolute left-1/2 top-1/2 h-2 w-2 -translate-x-1/2 -translate-y-1/2 rounded-full bg-cyan-400" />
    </div>
  );
}

const EXP = {
  EXPOSED: "border-amber-500/50 bg-amber-500/10 text-amber-200",
  PARTIAL: "border-yellow-400/30 bg-yellow-400/[0.06] text-yellow-200/90",
  SHELTERED: "border-white/10 bg-white/[0.02] text-slate-400",
};

export default function CoastDetail({ islandId, open, onOpenChange }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sat, setSat] = useState(null);
  const [satLoading, setSatLoading] = useState(false);
  const [satError, setSatError] = useState(false);

  useEffect(() => {
    if (!open || !islandId) return;
    setLoading(true);
    setData(null);
    setSat(null);
    setSatError(false);
    fetchIslandDetail(islandId)
      .then((d) => {
        setData(d);
        setSatLoading(true);
        setSat(sentinelUrl({ ...d.bbox, width: 640, height: 640, days: 75 }) + `&t=${Date.now()}`);
      })
      .finally(() => setLoading(false));
  }, [open, islandId]);

  const rc = data ? riskOf(data.risk) : riskOf("MINIMAL");
  const cd = data ? countdown(data.eta_utc) : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="glass-solid max-h-[90vh] max-w-3xl overflow-y-auto border-cyan-500/25 text-slate-100"
        data-testid="coast-detail-modal"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-xl font-extrabold tracking-tight text-white">
            {loading || !data ? "Loading coast detail…" : `${data.name} — Coast Detail`}
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            Named beaches tagged by exposure to the approaching Sargassum, with a 10 m Sentinel-2 scan.
          </DialogDescription>
        </DialogHeader>

        {loading || !data ? (
          <div className="flex h-40 items-center justify-center text-slate-400">
            <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Fetching advisory…
          </div>
        ) : (
          <div className="space-y-4">
            {/* summary row */}
            <div className="flex flex-wrap items-center gap-4">
              <CompassRose coast={data.impact_coast} />
              <div className="flex-1 space-y-2">
                <div className="flex items-center gap-2">
                  <span className={`rounded-md border px-2 py-0.5 font-mono-tel text-[11px] font-bold ${rc.chip}`}>
                    {rc.label}
                  </span>
                  <span className="inline-flex items-center gap-1 font-mono-tel text-sm text-amberx">
                    <Compass className="h-4 w-4" /> {data.impact_coast} coast exposed
                  </span>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <div className="glass rounded-lg p-2">
                    <div className="eyebrow flex items-center gap-1 text-slate-500"><Clock className="h-3 w-3" /> ETA</div>
                    <div className={`font-mono-tel text-sm font-bold ${rc.text}`}>{cd?.text}</div>
                  </div>
                  <div className="glass rounded-lg p-2">
                    <div className="eyebrow flex items-center gap-1 text-slate-500"><Waves className="h-3 w-3" /> Biomass</div>
                    <div className="font-mono-tel text-sm font-bold text-biomass">{data.biomass_tkm2}</div>
                  </div>
                  <div className="glass rounded-lg p-2">
                    <div className="eyebrow flex items-center gap-1 text-slate-500"><MapPin className="h-3 w-3" /> Exposed</div>
                    <div className="font-mono-tel text-sm font-bold text-amberx">{data.exposed_count} beaches</div>
                  </div>
                </div>
              </div>
            </div>

            {/* confidence + caveats */}
            <div className="glass rounded-lg p-3">
              <div className="mb-1.5 flex items-center justify-between">
                <span className="inline-flex items-center gap-1.5 eyebrow text-slate-400">
                  <Gauge className="h-3.5 w-3.5 text-cyanx" /> Forecast confidence
                </span>
                <span className="font-mono-tel text-sm font-bold" style={{ color: confColor(data.confidence) }}>
                  {data.confidence}%
                </span>
              </div>
              <div className="h-1.5 w-full overflow-hidden rounded-full bg-white/5">
                <div className="h-full rounded-full"
                  style={{ width: `${data.confidence}%`, background: confColor(data.confidence) }} />
              </div>
              {data.caveats?.length > 0 && (
                <ul className="mt-2 grid grid-cols-1 gap-1.5 sm:grid-cols-2">
                  {data.caveats.map((c, i) => (
                    <li key={i} className="flex items-start gap-1.5 text-[11px] leading-snug text-slate-400">
                      <Info className="mt-0.5 h-3 w-3 shrink-0 text-slate-500" />
                      <span>{c}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div>
              <div className="eyebrow mb-2 text-slate-400">Beaches by exposure</div>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                {data.beaches.map((b) => (
                  <div
                    key={b.name}
                    data-testid={`beach-${b.name.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                    className={`flex items-center justify-between rounded-lg border px-3 py-2 ${EXP[b.exposure]}`}
                  >
                    <div className="flex items-center gap-2">
                      {b.exposure === "EXPOSED" && <AlertTriangle className="h-3.5 w-3.5" />}
                      <span className="text-sm font-medium">{b.name}</span>
                    </div>
                    <div className="text-right">
                      <div className="font-mono-tel text-[10px] uppercase tracking-wide">{b.exposure}</div>
                      <div className="eyebrow text-slate-500">{b.coast}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* sentinel scan */}
            <div>
              <div className="eyebrow mb-2 flex items-center gap-1 text-slate-400">
                <Satellite className="h-3.5 w-3.5 text-cyan-400" /> Sentinel-2 · 10 m · biomass highlight
              </div>
              <div className="relative overflow-hidden rounded-lg border border-cyan-500/20">
                {satLoading && (
                  <div className="flex h-40 items-center justify-center text-[12px] text-slate-400">
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Requesting L2A tile (can take up to a minute)…
                  </div>
                )}
                {satError && (
                  <div className="flex h-40 items-center justify-center text-[12px] text-amber-400">
                    No cloud-free tile found for this window.
                  </div>
                )}
                {sat && !satError && (
                  <img
                    src={sat}
                    alt="Sentinel-2 scan"
                    className={`w-full ${satLoading ? "hidden" : "block"}`}
                    data-testid="coast-sentinel-image"
                    onLoad={() => setSatLoading(false)}
                    onError={() => {
                      setSatLoading(false);
                      setSatError(true);
                    }}
                  />
                )}
                {sat && !satError && !satLoading && data.bbox && (
                  <div className="pointer-events-none absolute inset-0" data-testid="beach-pins">
                    {data.beaches.map((b) => {
                      const bb = data.bbox;
                      const left = ((b.lng - bb.west) / (bb.east - bb.west)) * 100;
                      const top = ((bb.north - b.lat) / (bb.north - bb.south)) * 100;
                      if (left < 1 || left > 99 || top < 1 || top > 99) return null;
                      const col = PIN[b.exposure];
                      return (
                        <div
                          key={b.name}
                          data-testid={`beach-pin-${b.name.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                          className="absolute flex -translate-x-1/2 -translate-y-1/2 flex-col items-center"
                          style={{ left: `${left}%`, top: `${top}%` }}
                        >
                          <span
                            className="h-2.5 w-2.5 rounded-full ring-2 ring-black/50"
                            style={{ background: col, boxShadow: `0 0 8px ${col}` }}
                          />
                          {b.exposure !== "SHELTERED" && (
                            <span
                              className="mt-0.5 whitespace-nowrap rounded bg-black/70 px-1 text-[8px] font-mono-tel font-bold"
                              style={{ color: col }}
                            >
                              {b.name}
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
              <div className="mt-1.5 flex flex-wrap items-center gap-3 text-[10px] text-slate-500">
                {Object.entries(PIN).map(([k, c]) => (
                  <span key={k} className="inline-flex items-center gap-1">
                    <span className="h-2 w-2 rounded-full" style={{ background: c }} /> {k.toLowerCase()}
                  </span>
                ))}
                <span className="ml-auto">Pins are indicative beach locations</span>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
